import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Heart, Briefcase, User, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMULES = [
  {
    id: 'olfactif',
    icon: <Sparkles size={28} />,
    label: 'Coaching olfactif',
    subtitle: 'L\'entrée — découvrir qui vous êtes par le parfum',
    description:
      "Une parenthèse sensorielle pour reconnecter avec vos émotions, vos souvenirs et votre identité profonde à travers la fragrance. Le parfum devient un miroir et un outil de connaissance de soi.",
    duration: '1h en boutique ou en visio',
    color: '#B8922A',
  },
  {
    id: 'emotions',
    icon: <Heart size={28} />,
    label: 'Gestion des émotions & du stress',
    subtitle: 'Pour retrouver l\'équilibre intérieur',
    description:
      "Approches pratiques et personnalisées pour mieux comprendre vos émotions, gérer le stress du quotidien et retrouver un état d'apaisement durable. Méthodes validées scientifiquement, ancrées dans le vécu.",
    duration: 'Séances de 1h, en présentiel ou visio',
    color: '#1E7A6A',
  },
  {
    id: 'cnv',
    icon: <Briefcase size={28} />,
    label: 'Communication non violente',
    subtitle: 'Relations apaisées · écoute · empathie',
    description:
      "Apprenez à exprimer vos besoins, écouter ceux des autres et désamorcer les conflits — au sein du couple, de la famille, des équipes professionnelles. Un outil qui change profondément la qualité des relations.",
    duration: 'Ateliers ou sessions individuelles · sur-mesure',
    color: '#5C2018',
  },
  {
    id: 'femme-entrepreneure',
    icon: <User size={28} />,
    label: 'Femme · Mère · Entrepreneure',
    subtitle: 'Ma signature — l\'accompagnement de celles qui portent tout',
    description:
      "Vous jonglez entre votre entreprise, votre famille, vos rêves, vos doutes ? Cet accompagnement est pensé pour vous. Inspiré de mon propre parcours, basé sur l'écoute, des outils concrets et beaucoup de douceur.",
    duration: 'Programme 3 ou 6 mois · accompagnement régulier',
    color: '#5C2018',
  },
];

const CoachingPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [interests, setInterests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleInterest = (id) => {
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/coaching/waitlist`, { email, name, interests });
      setSubmitted(true);
      toast.success('Merci ! Vous êtes inscrit·e à la liste d\'attente.');
    } catch (err) {
      toast.error('Une erreur est survenue. Merci de réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-20">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-[#FDFAF4] border-b border-[#B8922A]/20 overflow-hidden" data-testid="coaching-hero">
        <img
          src="/assets/rose-filigrane.jpg"
          alt=""
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] object-contain pointer-events-none select-none mix-blend-multiply opacity-[0.07]"
        />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
            Accompagnement · Bientôt disponible
          </p>
          <h1
            className="text-5xl md:text-6xl font-light mb-6 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            data-testid="coaching-title"
          >
            Coaching Harmony
          </h1>
          <p className="text-lg md:text-xl text-[#332724] font-light leading-relaxed max-w-3xl mx-auto">
            De l'identité olfactive à l'équilibre intérieur. Un accompagnement humain et
            personnalisé, par <strong>Hayat</strong>, fondatrice d'Harmony Parfumerie
            — diplômée en gestion du stress, des émotions et en communication non violente.
          </p>
          <a
            href="/assets/plaquette-coaching-harmony.pdf"
            download="Coaching-Harmony-Plaquette.pdf"
            className="inline-flex items-center gap-2 mt-8 px-8 py-3 rounded-full border border-[#B8922A] text-[#5C2018] hover:bg-[#B8922A]/10 uppercase tracking-[0.2em] text-sm transition-colors"
            data-testid="coaching-plaquette-download"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Télécharger la plaquette
          </a>
        </div>
      </section>

      {/* Pont parfum → coaching */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p
            className="text-2xl md:text-3xl italic text-[#5C2018] leading-relaxed"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            « On commence par retrouver son identité olfactive.
            <br />
            On continue par retrouver son équilibre intérieur. »
          </p>
          <p className="mt-6 text-[#332724] font-light max-w-2xl mx-auto">
            Le parfum est une porte d'entrée vers soi. Chez Harmony, nous prolongeons cette
            écoute attentive bien au-delà de la fragrance — vers ce qui vous fait vibrer,
            vers ce qui vous fragilise parfois, vers ce qui vous porte chaque jour.
          </p>
        </div>
      </section>

      {/* Formules */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-light text-center mb-4 text-[#5C2018]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Nos formules
          </h2>
          <p className="text-center text-[#332724]/70 font-light mb-12 max-w-2xl mx-auto">
            Quatre portes d'entrée selon vos besoins. Toutes pensées pour vous accompagner
            avec écoute, méthode et bienveillance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FORMULES.map((f) => (
              <div
                key={f.id}
                className="bg-white p-8 rounded-2xl border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]"
                data-testid={`formule-${f.id}`}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-4 text-[#FDFAF4]"
                  style={{ backgroundColor: f.color }}
                >
                  {f.icon}
                </div>
                <p className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: f.color }}>
                  {f.subtitle}
                </p>
                <h3
                  className="text-2xl text-[#5C2018] mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
                >
                  {f.label}
                </h3>
                <p className="text-[#332724] font-light leading-relaxed mb-3">
                  {f.description}
                </p>
                <p className="text-sm text-[#1E7A6A] italic">{f.duration}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Liste d'attente */}
      <section className="py-20 px-6 bg-[#332724] text-[#FDFAF4] mt-12">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">
            Lancement prochain
          </p>
          <h2
            className="text-4xl md:text-5xl font-light mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            Soyez les premièr·e·s informé·e·s
          </h2>
          <p className="text-lg font-light text-[#FDFAF4]/85 mb-8 leading-relaxed">
            Le coaching Harmony arrive très bientôt. Inscrivez-vous à la liste d'attente
            pour recevoir le lancement en avant-première et bénéficier d'un tarif privilégié.
          </p>

          {submitted ? (
            <div className="bg-[#FDFAF4]/10 border border-[#B8922A] rounded-2xl p-8 text-center">
              <Check className="text-[#B8922A] mx-auto mb-3" size={36} />
              <p className="text-xl mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Bienvenue dans le cercle Harmony
              </p>
              <p className="text-sm text-[#FDFAF4]/75">
                Vous recevrez un email dès l'ouverture des inscriptions.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Votre prénom"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="px-4 py-3 rounded-full bg-[#FDFAF4]/10 border border-[#FDFAF4]/30 text-[#FDFAF4] placeholder-[#FDFAF4]/50 outline-none focus:border-[#B8922A]"
                  data-testid="waitlist-name"
                />
                <input
                  required
                  type="email"
                  placeholder="Votre email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="px-4 py-3 rounded-full bg-[#FDFAF4]/10 border border-[#FDFAF4]/30 text-[#FDFAF4] placeholder-[#FDFAF4]/50 outline-none focus:border-[#B8922A]"
                  data-testid="waitlist-email"
                />
              </div>
              <p className="text-sm text-[#FDFAF4]/70 text-left">
                Quels accompagnements vous intéressent ? (optionnel)
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {FORMULES.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleInterest(f.id)}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] border transition-colors ${
                      interests.includes(f.id)
                        ? 'bg-[#B8922A] text-[#FDFAF4] border-[#B8922A]'
                        : 'bg-transparent text-[#FDFAF4] border-[#FDFAF4]/30 hover:border-[#B8922A]'
                    }`}
                    data-testid={`interest-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#B8922A] hover:bg-[#9a7922] text-[#FDFAF4] px-8 py-3 rounded-full uppercase tracking-[0.2em] text-sm transition-colors disabled:opacity-50"
                data-testid="waitlist-submit"
              >
                {submitting ? 'Envoi…' : 'Rejoindre la liste d\'attente'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default CoachingPage;
